import { Areas, LogicalSpace, Obstacle } from '@thive/fddm-types';
import { logger } from '@common/logging/logger';

/**
 * @description Pure store updates, usable on the current state or on a `transact` draft,
 * so several of them can be written to the store in one go.
 */
export const StoreUtils = {
  /**
   * @description Merges the area updates into the existing collections.
   * Returns the new collections and the merged areas that were found (unknown ids are skipped).
   */
  updateAreas: (updated: Areas, areas: Areas): { areas: Areas; updatedAreas: Areas } => {
    const areaMaps = {
      highwayAreas: new Map(areas.highwayAreas.map((item) => [item.id, item])),
      rackingAreas: new Map(areas.rackingAreas.map((item) => [item.id, item])),
      singularStationAreas: new Map(areas.singularStationAreas.map((item) => [item.id, item])),
      stationaryAreas: new Map(areas.stationaryAreas.map((item) => [item.id, item])),
      manualAreas: new Map(areas.manualAreas.map((item) => [item.id, item])),
    };
    const updatedAreas: Areas = {
      highwayAreas: [],
      rackingAreas: [],
      singularStationAreas: [],
      stationaryAreas: [],
      manualAreas: [],
    };

    updated.highwayAreas.forEach((area) => {
      const areaToUpdate = areaMaps.highwayAreas.get(area.id);
      if (!areaToUpdate) {
        logger.warn(`Could not update area with id: ${area.id}. Not found.`);
        return;
      }

      const updated = {
        ...areaToUpdate,
        ...area,
      };
      updatedAreas.highwayAreas.push(updated);
      areaMaps.highwayAreas.set(updated.id, updated);
    });
    updated.rackingAreas.forEach((area) => {
      const areaToUpdate = areaMaps.rackingAreas.get(area.id);
      if (!areaToUpdate) {
        logger.warn(`Could not update area with id: ${area.id}. Not found.`);
        return;
      }

      const updated = {
        ...areaToUpdate,
        ...area,
      };
      updatedAreas.rackingAreas.push(updated);
      areaMaps.rackingAreas.set(updated.id, updated);
    });
    updated.singularStationAreas.forEach((area) => {
      const areaToUpdate = areaMaps.singularStationAreas.get(area.id);
      if (!areaToUpdate) {
        logger.warn(`Could not update area with id: ${area.id}. Not found.`);
        return;
      }

      const updated = {
        ...areaToUpdate,
        ...area,
      };
      updatedAreas.singularStationAreas.push(updated);
      areaMaps.singularStationAreas.set(updated.id, updated);
    });
    updated.stationaryAreas.forEach((area) => {
      const areaToUpdate = areaMaps.stationaryAreas.get(area.id);
      if (!areaToUpdate) {
        logger.warn(`Could not update area with id: ${area.id}. Not found.`);
        return;
      }

      const updated = {
        ...areaToUpdate,
        ...area,
      };
      updatedAreas.stationaryAreas.push(updated);
      areaMaps.stationaryAreas.set(updated.id, updated);
    });
    updated.manualAreas.forEach((area) => {
      const areaToUpdate = areaMaps.manualAreas.get(area.id);
      if (!areaToUpdate) {
        logger.warn(`Could not update area with id: ${area.id}. Not found.`);
        return;
      }
      const updated = {
        ...areaToUpdate,
        ...area,
      };
      updatedAreas.manualAreas.push(updated);
      areaMaps.manualAreas.set(updated.id, updated);
    });

    return {
      areas: {
        highwayAreas: [...areaMaps.highwayAreas.values()],
        rackingAreas: [...areaMaps.rackingAreas.values()],
        singularStationAreas: [...areaMaps.singularStationAreas.values()],
        stationaryAreas: [...areaMaps.stationaryAreas.values()],
        manualAreas: [...areaMaps.manualAreas.values()],
      },
      updatedAreas,
    };
  },

  updateObstacles: (updates: Obstacle[], obstacles: Obstacle[]): Obstacle[] =>
    updateItemsById(updates, obstacles, 'obstacle'),

  updateLogicalSpaces: (updates: LogicalSpace[], logicalSpaces: LogicalSpace[]): LogicalSpace[] =>
    updateItemsById(updates, logicalSpaces, 'logical space'),
};

/**
 * @description Merges each update into the item with the same id, unknown ids are skipped.
 */
const updateItemsById = <Item extends { id: string }>(
  updates: Item[],
  items: Item[],
  itemName: string,
): Item[] => {
  const updatesById = new Map(updates.map((update) => [update.id, update]));
  const existingIds = new Set(items.map(({ id }) => id));

  updates
    .filter(({ id }) => !existingIds.has(id))
    .forEach(({ id }) => logger.warn(`Could not update ${itemName} with id: ${id}. Not found.`));

  return items.map((item) => {
    const update = updatesById.get(item.id);
    return update ? { ...item, ...update } : item;
  });
};
